import { useQuery, type ApolloError } from "@apollo/client";
import { useMemo } from "react";
import { CompanyFilter } from "../../../../../legacy/types/companyFilter";
import { SEGMENT_ANALYSIS_COUNTRY_BREAKDOWN_QUERY } from "../../../../../legacy/queries/MI";
import {
  CountryBreakdownPayload,
  CountryBreakdownVariables,
  SpendBreakdownResult,
} from "../../../../../legacy/types/companySearch/MI";
import { COMPANY_SEARCH_CLIENT } from "../../../../../utils/consts";
import { useTaxonomyContext } from "../../../../../contexts/Taxonomy/TaxonomyContext";
import { useWorkspaceStore } from "../../../../../store/workspace/WorkspaceStore";
import type {
  ModelScoreInput,
  ModelScoreFilterInput,
} from "../../../../../generated/company-search";

interface GetSpendByCountryDataProps {
  opportunityGeneratorFilters: CompanyFilter;
  customSpendCategory?: {
    name: string;
    categories: Array<{ id: string; weight: number }>;
  };
  localizeInstalls?: boolean;
  /** V2 ("model tier") scoring narrowing — mirrors opportunityGeneratorSearch's
   * args. */
  tenantId?: string;
  modelScores?: ModelScoreInput[];
  modelScoreFilter?: ModelScoreFilterInput[];
  /** Share-page panel tag (Apollo context); see toPanelDescriptor. */
  activeModelId?: string;
  /** Skips the query entirely. Omitted (undefined) behaves exactly like
   * `false` — existing callers that never pass this see no change. */
  skip?: boolean;
  /** Row-count cap, forwarded as-is to hermes. Omitted (undefined) behaves
   * exactly like every caller before this field existed (hermes' own
   * default of 10) — e.g. Market Landscape; Market Sizing's charts pass 20. */
  limit?: number;
}

interface SpendByCountryChartData {
  loading: boolean;
  error?: ApolloError;
  spendByCountryChartData: SpendBreakdownResult[];
}

export function useGetSpendByCountryData(
  props: GetSpendByCountryDataProps
): SpendByCountryChartData {
  const { countries } = useTaxonomyContext();
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  const [countryNameMap, countryIdMap] = useMemo(() => {
    const nameMap = new Map();
    const idMap = new Map();
    for (const country of countries || []) {
      idMap.set(country.id, { name: country.name, alpha2: country.alpha2 });
      nameMap.set(country.name, { alpha2: country.alpha2, id: country.id });
    }
    return [nameMap, idMap];
  }, [countries]);

  const {
    data,
    loading: isLoading,
    error,
  } = useQuery<CountryBreakdownPayload, CountryBreakdownVariables>(
    SEGMENT_ANALYSIS_COUNTRY_BREAKDOWN_QUERY,
    {
      variables: {
        companyFilters: props.opportunityGeneratorFilters,
        localizeInstalls: props.localizeInstalls,
        customSpendCategory: props.customSpendCategory,
        companyMatchFile:
          props.opportunityGeneratorFilters?.opportunityType &&
          workspaceWhitespace?.fileId &&
          workspaceWhitespace?.lastMatched
            ? workspaceWhitespace
            : undefined,
        tenantId: props.tenantId,
        modelScores: props.modelScores,
        modelScoreFilter: props.modelScoreFilter,
        limit: props.limit,
      },
      context: {
        clientName: COMPANY_SEARCH_CLIENT,
        activeModelId: props.activeModelId,
      },
      skip: props.skip,
    }
  );

  const spendBreakDownResult = useMemo<Array<SpendBreakdownResult>>(
    () =>
      data?.segmentAnalysisCountryBreakdown.map(
        ({ companyCount, totalSpend, countryId, countryName }) => ({
          id: countryName
            ? countryNameMap.get(countryName)?.alpha2
            : countryIdMap.get(countryId)?.alpha2,
          companies: companyCount,
          dimension:
            countryName || countryIdMap.get(countryId)?.name || "unknown",
          spend: totalSpend,
        })
      ) ?? [],
    [countryIdMap, countryNameMap, data?.segmentAnalysisCountryBreakdown]
  );

  return {
    loading: isLoading,
    error,
    spendByCountryChartData: spendBreakDownResult,
  };
}
